// src/lib/auth/signin-redirect.ts
//
// Where a signed-out visitor goes, and what they are told when they get there.
//
// Thirty-two server pages call `redirect('/signin')` with no callbackUrl, so a
// visitor who clicks Practice lands on a bare sign-in form and — having signed
// in — is dropped on the dashboard rather than the thing they asked for. They
// have to find it again, and nothing on the way explains why they were stopped.
//
// WHY REASONS ARE A CLOSED SET, not free text.
// The obvious shape is `?message=Create+a+free+account…` and render it. That
// turns the sign-in page into a billboard anyone can write on: a crafted link
// (`/signin?message=Your+account+was+locked,+confirm+your+card`) would render
// attacker-chosen copy on our own sign-in form, next to a real password field.
// So the URL carries a KEY, the copy lives here, and an unrecognised key shows
// nothing at all.
import type { Route } from 'next'

/**
 * Keys a page may pass. Anything else renders no banner.
 *
 * Only the surfaces that actually pass one are listed. Thirty other pages still
 * redirect without a reason — they get the plain sign-in form, exactly as
 * before — and adding keys for them in advance would be copy nobody has chosen
 * sitting in a file, read as though someone had.
 */
export type SigninReason = 'practice'

const REASON_COPY: Record<SigninReason, string> = {
  practice:
    'Create a free account to start topic practice — the question bank is unlimited on the free plan.',
}

/**
 * The copy for a reason key, or null when absent/unrecognised.
 *
 * `Object.hasOwn`, not a plain lookup: an object literal inherits from
 * Object.prototype, so `REASON_COPY['constructor']` returns the Object
 * constructor and `['toString']` a function — both truthy, both rendered. A
 * `?reason=constructor` link put "[Function Object]" on the sign-in form until
 * a test went looking for it. An own-property check makes the set actually
 * closed rather than closed-looking.
 */
export function signinReasonCopy(reason: string | null | undefined): string | null {
  if (!reason || !Object.hasOwn(REASON_COPY, reason)) return null
  return REASON_COPY[reason as SigninReason]
}

/**
 * Build the sign-in URL for a page that just turned a visitor away.
 *
 * `callbackUrl` is the param NextAuth and the sign-in page already read, so the
 * visitor returns to where they were going instead of the dashboard.
 */
export function signinHref(returnTo: string, reason?: SigninReason): Route {
  const params = new URLSearchParams({ callbackUrl: returnTo })
  if (reason) params.set('reason', reason)
  return `/signin?${params.toString()}` as Route
}
